import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {aiMetricsService} from '@/services/aiMetricsService'
import type {RootState} from '@/store'

export interface AccessContext {
  type: string
  delegatorName: string
  delegatorEmail: string
  capabilities: string[]
  delegationId?: string
}

export interface AccessSummaryData {
  canViewAiProductivityTab: boolean
  canManageAccess: boolean
  canManageMembers: boolean
  contexts: AccessContext[]
}

interface AccessSummaryState {
  data: AccessSummaryData | null
  loading: boolean
  error: string | null
}

const initialState: AccessSummaryState = {
  data: null,
  loading: false,
  error: null,
}

export const fetchAccessSummary = createAsyncThunk('accessSummary/fetchAccessSummary', async (_, {rejectWithValue}) => {
  try {
    const data = await aiMetricsService.getAccessDelegationsSummary()
    return data as AccessSummaryData
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch access summary')
  }
})

const accessSummarySlice = createSlice({
  name: 'accessSummary',
  initialState,
  reducers: {
    resetAccessSummary: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAccessSummary.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAccessSummary.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchAccessSummary.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {resetAccessSummary} = accessSummarySlice.actions

export const selectAccessSummary = (state: RootState) => state.accessSummary.data
export const selectAccessSummaryLoading = (state: RootState) => state.accessSummary.loading

export default accessSummarySlice.reducer
